import TrackArtistCredits from "./TrackArtistCredits";
import BpmInput from "../ui/BpmInput";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import MenuSurface from "../ui/MenuSurface";
import MenuItem from "@mui/material/MenuItem";
import useEditorForm from "../hooks/useEditorForm";
import SelectInput from "../ui/SelectInput";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Button from "@mui/material/Button";
import TextInput from "../ui/TextInput";
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Link from "../ui/RouterLink";
import TagDropdown from "./TagDropdown";
import useAsyncAction from "../hooks/useAsyncAction";
import { describeApiError } from "../api/request";
import {
  normalizeBreaks,
  normalizeTags,
  trackFlagText,
  resolveTrackArtists,
} from "../utils";
function TrackCard({
  readOnly = false,
  track,
  onSave,
  canEditDuration,
  canEditTrackInfo,
  highlight,
  role,
  tagOptions = null,
  onCreateTag,
  breakOptions = [],
}) {
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const action = useAsyncAction();
  const tagSelectionEnabled = tagOptions !== null;
  const makeFields = React.useCallback(
    (t) => ({
      position: t.position || "",
      title: t.title || "",
      duration: t.duration || "",
      bpm: t.bpm ?? "",
      rating: t.love ? "love" : t.like ? "like" : t.hate ? "hate" : "",
      is_instrumental: !!t.is_instrumental,
      breaks: normalizeBreaks(t.breaks),
      notes: t.notes || "",
      tags: tagSelectionEnabled ? normalizeTags(t.tags) : t.tags || "",
    }),
    [tagSelectionEnabled],
  );
  const [editing, setEditing] = React.useState(false);
  const [fields, setFields, editorForm] = useEditorForm(makeFields(track));
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const resolvedArtists = React.useMemo(
    () => resolveTrackArtists(track),
    [track],
  );
  React.useEffect(() => {
    setFields((previous) =>
      editing
        ? {
            ...previous,
            tags: tagSelectionEnabled
              ? normalizeTags(previous.tags)
              : Array.isArray(previous.tags)
                ? previous.tags.join(", ")
                : previous.tags,
          }
        : makeFields(track),
    );
  }, [track, makeFields, editing, tagSelectionEnabled, setFields]);
  const handleChange = (field, value) => {
    setFields((f) => ({
      ...f,
      [field]: value,
    }));
  };
  const handleSave = async () => {
    const data = {
      ...(canEditDuration
        ? {
            duration: fields.duration,
          }
        : {}),
      ...(canEditTrackInfo
        ? {
            title: fields.title,
            position: fields.position,
          }
        : {}),
      bpm: fields.bpm,
      like: fields.rating === "like",
      love: fields.rating === "love",
      hate: fields.rating === "hate",
      is_instrumental: fields.is_instrumental,
      notes: fields.notes,
      tags: tagOptions ? fields.tags.join(", ") : fields.tags,
    };
    if (Array.isArray(fields.breaks)) {
      data.breaks = fields.breaks;
    }
    if (await action.run(() => onSave(track.id, data))) setEditing(false);
  };
  const handleCancel = () => {
    action.clearError();
    setFields(makeFields(track));
    setEditing(false);
  };
  const handleMenuToggle = (e) => {
    if (menuOpen) {
      e.currentTarget.blur();
    }
    setMenuOpen(!menuOpen);
  };
  const toggleField = async (field) => {
    await action.run(() =>
      onSave(track.id, {
        [field]: !track[field],
      }),
    );
    setMenuOpen(false);
  };
  const resolvedBpm = track.bpm ?? track.default_bpm;
  const isDefaultBpm = track.bpm == null && track.default_bpm != null;
  return (
    <div
      className={`track-card${highlight ? " highlight" : ""}${editing ? " editing" : ""}`}
      aria-busy={action.pending}
    >
      {action.error && (
        <Alert role="alert" severity="error">
          {describeApiError(action.error)}
        </Alert>
      )}
      <div className="track-header">
        <span className="track-side">
          {editing && canEditTrackInfo ? (
            <TextInput
              className="position-input"
              type="text"
              value={fields.position}
              aria-label="Position"
              disabled={action.pending}
              onChange={(e) => handleChange("position", e.target.value)}
            />
          ) : (
            track.position
          )}
        </span>
        <span className="track-title-group">
          {editing && canEditTrackInfo ? (
            <TextInput
              type="text"
              value={fields.title}
              aria-label="Track title"
              disabled={action.pending}
              onChange={(e) => handleChange("title", e.target.value)}
              className="title-input"
              multiline
              maxRows={3}
            />
          ) : (
            <span className="track-title">{track.title}</span>
          )}
          {!editing && resolvedArtists.length > 0 && (
            <span className="track-subtitle">
              {resolvedArtists.map((artist, index) => (
                <React.Fragment key={artist.id ?? `${artist.name}-${index}`}>
                  {index > 0 && ", "}
                  {artist.id && !readOnly ? (
                    <Link className="artist-link" to={`/artist/${artist.id}`}>
                      {artist.name}
                    </Link>
                  ) : (
                    <span>{artist.name}</span>
                  )}
                </React.Fragment>
              ))}
            </span>
          )}
          {trackFlagText(track) && (
            <span className="track-subtitle">{trackFlagText(track)}</span>
          )}
          {!editing && <TrackArtistCredits track={track} readOnly={readOnly} />}
        </span>
        {!editing && !readOnly && (
          <div className="menu track-menu" ref={menuRef}>
            <IconButton
              type="button"
              className="menu-toggle"
              onClick={(event) => {
                setMenuAnchor(event.currentTarget);
                handleMenuToggle(event);
              }}
              aria-label="Track options"
              aria-expanded={menuOpen}
              disabled={action.pending}
              aria-haspopup="menu"
            >
              <MoreVertIcon />
            </IconButton>
            {menuOpen && (
              <MenuSurface
                anchorEl={menuAnchor}
                onClose={() => setMenuOpen(false)}
              >
                <MenuItem
                  onClick={() => {
                    setEditing(true);
                    setMenuOpen(false);
                  }}
                  component="button"
                >
                  Edit Track
                </MenuItem>
                {(role === "admin" || role === "user") && (
                  <>
                    <MenuItem
                      onClick={() => toggleField("is_instrumental")}
                      component="button"
                    >
                      Set Instrumental{" "}
                      {track.is_instrumental ? "False" : "True"}
                    </MenuItem>
                    <MenuItem
                      onClick={() => toggleField("is_a_cappella")}
                      component="button"
                    >
                      Set A Cappella {track.is_a_cappella ? "False" : "True"}
                    </MenuItem>
                  </>
                )}
                <MenuItem
                  onClick={() => {
                    const ret = encodeURIComponent(
                      location.pathname + location.search,
                    );
                    navigate(`/add-to-set?track=${track.id}&return=${ret}`);
                    setMenuOpen(false);
                  }}
                  component="button"
                >
                  Add To Set
                </MenuItem>
              </MenuSurface>
            )}
          </div>
        )}
        {editing && (
          <IconButton
            type="button"
            className="close-btn"
            onClick={handleCancel}
            aria-label="Cancel editing"
            disabled={action.pending}
          >
            <CloseIcon />
          </IconButton>
        )}
      </div>
      {editing ? (
        <div className="track-meta">
          <div className="meta-row pair">
            <div className="field-left">
              <span>Duration</span>
              {canEditDuration ? (
                <TextInput
                  className="duration-input"
                  type="text"
                  value={fields.duration}
                  aria-label="Duration"
                  disabled={action.pending}
                  onChange={(e) => handleChange("duration", e.target.value)}
                />
              ) : (
                <span>{track.duration}</span>
              )}
            </div>
            <div className="field-right">
              <span>BPM</span>
              <BpmInput
                className="bpm-input"
                value={fields.bpm}
                aria-label="BPM"
                disabled={action.pending}
                onChange={(e) => handleChange("bpm", e.target.value)}
              />
            </div>
          </div>
          <div className="meta-row pair">
            <div className="field-left">
              <span>Rating</span>
              <SelectInput
                value={fields.rating}
                aria-label="Rating"
                disabled={action.pending}
                onChange={(e) => handleChange("rating", e.target.value)}
              >
                <option value=""></option>
                <option value="like">👍</option>
                <option value="love">❤️</option>
                <option value="hate">❌</option>
              </SelectInput>
            </div>
          </div>
          <div className="meta-row">
            <span>Notes</span>
            <TextInput
              type="text"
              value={fields.notes}
              aria-label="Notes"
              multiline
              minRows={2}
              maxRows={5}
              disabled={action.pending}
              onChange={(e) => handleChange("notes", e.target.value)}
            />
          </div>
          <div className="meta-row">
            <span>Tags</span>
            {tagOptions ? (
              <TagDropdown
                disabled={action.pending}
                options={tagOptions}
                selected={fields.tags}
                onChange={(opts) => handleChange("tags", opts)}
                onCreate={onCreateTag}
              />
            ) : (
              <TextInput
                type="text"
                value={fields.tags}
                aria-label="Tags"
                disabled={action.pending}
                onChange={(e) => handleChange("tags", e.target.value)}
              />
            )}
          </div>
          <div className="meta-row actions">
            <Button
              type="button"
              className="btn"
              onClick={editorForm.handleSubmit(handleSave)}
              disabled={action.pending}
            >
              {action.pending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="track-meta">
          {track.duration && (
            <div className="meta-row">
              <span>Duration</span>
              <span>{track.duration}</span>
            </div>
          )}
          {resolvedBpm != null && (
            <div className="meta-row">
              <span>BPM</span>
              <span className={isDefaultBpm ? "default-bpm" : ""}>
                {resolvedBpm}
              </span>
            </div>
          )}
          {(track.love || track.like || track.hate) && (
            <div className="meta-row">
              <span>Rating</span>
              <span>
                {track.love ? "❤️" : track.like ? "👍" : track.hate ? "❌" : ""}
              </span>
            </div>
          )}
          {track.notes && (
            <div className="meta-row">
              <span>Notes</span>
              <span>{track.notes}</span>
            </div>
          )}
          {track.tags && (
            <div className="meta-row">
              <span>Tags</span>
              <span>{track.tags}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
export default TrackCard;
